/**
 * CMS 콘텐츠(필드우선) → 홈페이지 컴포넌트가 쓰는 형태(언어우선) 어댑터.
 *
 * 배경:
 *  - 홈페이지 컴포넌트는 언어우선 구조를 소비한다.
 *      weDo.highResolution[lang] = { title, description, images, videos }
 *      constellation[lang] = { title, description }   (+ schedule/매핑은 정적)
 *      weService[lang] = { title, description }        (+ services 배열은 정적)
 *  - CMS(home.json)·어드민 폼은 편집 편의를 위해 필드우선 구조를 쓴다.
 *      { title: { KR, EN }, description: { KR, EN } }
 *  - 관리자는 텍스트만 편집하고 이미지·비디오·schedule·services는 편집하지 않는다.
 *
 * 전략: homeLang(언어우선 + 정적 자산)을 베이스로 두고,
 *       CMS가 준 텍스트(title/description)만 언어별로 덮어씌운다.
 *       CMS 값이 없으면 homeLang 값을 그대로 사용(무중단).
 */
import {
  weDo as fbWeDo,
  constellation as fbConstellation,
  weService as fbWeService,
} from "../../../../lang/renewal/homeLang";

const LANGS = ["KR", "EN"];

/** 언어우선 base에 CMS 필드우선(title/description) 텍스트를 언어별로 덮어씌움 */
function overlayTitleDesc(base, cms) {
  const out = { ...base };
  for (const lang of LANGS) {
    out[lang] = {
      ...(base[lang] ?? {}),
      ...(cms?.title?.[lang] != null ? { title: cms.title[lang] } : {}),
      ...(cms?.description?.[lang] != null
        ? { description: cms.description[lang] }
        : {}),
    };
  }
  return out;
}

/** highResolution: 이미지 1개({src,width,height}) — CMS image(언어별 url)로 src만 교체, 크기 유지 */
function adaptHighRes(fb, cms) {
  const out = {};
  for (const lang of LANGS) {
    const fbL = fb[lang] ?? {};
    const cmsImg = cms?.image?.[lang];
    out[lang] = {
      ...fbL,
      ...(cms?.title?.[lang] != null ? { title: cms.title[lang] } : {}),
      ...(cms?.description?.[lang] != null
        ? { description: cms.description[lang] }
        : {}),
      images: cmsImg
        ? [{ ...(fbL.images?.[0] ?? {}), src: cmsImg }]
        : fbL.images,
    };
  }
  return out;
}

/** observationMode: images[4] + videos.desktop[4]/mobile[4] — CMS 언어별 배열로 교체 */
function adaptObs(fb, cms) {
  const out = {};
  for (const lang of LANGS) {
    const fbL = fb[lang] ?? {};
    out[lang] = {
      ...fbL,
      ...(cms?.title?.[lang] != null ? { title: cms.title[lang] } : {}),
      ...(cms?.description?.[lang] != null
        ? { description: cms.description[lang] }
        : {}),
      images: cms?.images?.[lang]?.length ? cms.images[lang] : fbL.images,
      videos: {
        desktop: cms?.videosDesktop?.[lang]?.length
          ? cms.videosDesktop[lang]
          : fbL.videos?.desktop,
        mobile: cms?.videosMobile?.[lang]?.length
          ? cms.videosMobile[lang]
          : fbL.videos?.mobile,
      },
    };
  }
  return out;
}

/** lightWeight: 이미지 1개(url 문자열 배열) */
function adaptLight(fb, cms) {
  const out = {};
  for (const lang of LANGS) {
    const fbL = fb[lang] ?? {};
    const cmsImg = cms?.image?.[lang];
    out[lang] = {
      ...fbL,
      ...(cms?.title?.[lang] != null ? { title: cms.title[lang] } : {}),
      ...(cms?.description?.[lang] != null
        ? { description: cms.description[lang] }
        : {}),
      images: cmsImg ? [cmsImg] : fbL.images,
    };
  }
  return out;
}

/** weDo: title(평문) + 3개 feature(텍스트 + 이미지/비디오 모두 언어별 병합) */
export function adaptWeDo(cms) {
  return {
    ...fbWeDo,
    title: typeof cms?.title === "string" ? cms.title : fbWeDo.title,
    highResolution: adaptHighRes(fbWeDo.highResolution, cms?.highResolution),
    observationMode: adaptObs(fbWeDo.observationMode, cms?.observationMode),
    lightWeight: adaptLight(fbWeDo.lightWeight, cms?.lightWeight),
  };
}

/** constellation: 언어우선 텍스트 + schedule/numberMapping/yearMapping 정적 */
export function adaptConstellation(cms) {
  return overlayTitleDesc(fbConstellation, cms);
}

/** 단일 URL(fallback) + CMS 언어별 URL({KR,EN}) → 언어별 URL로 병합 */
function overlayUrl(fbUrl, cmsLocalized) {
  return {
    KR: cmsLocalized?.KR ?? fbUrl,
    EN: cmsLocalized?.EN ?? fbUrl,
  };
}

/**
 * weService: 헤더 텍스트(언어우선) + services 배열.
 * services 각 항목의 image/video는 homeLang에선 단일 URL이지만,
 * CMS는 언어별로 관리 → 컴포넌트가 [langValue]로 읽도록 언어별 형태로 변환.
 *   services[i] = { title:{KR,EN}, image:{KR,EN}, videoDesktop:{KR,EN}, videoMobile:{KR,EN} }
 */
export function adaptWeService(cms) {
  const base = overlayTitleDesc(fbWeService, cms);
  const services = (fbWeService.services ?? []).map((fb, i) => {
    const c = cms?.services?.[i];
    return {
      title: { ...fb.title, ...(c?.title ?? {}) },
      image: overlayUrl(fb.image, c?.image),
      videoDesktop: overlayUrl(fb.video?.desktop, c?.videoDesktop),
      videoMobile: overlayUrl(fb.video?.mobile, c?.videoMobile),
    };
  });
  return { ...base, services };
}
